import { useEffect, useState, useCallback } from "react";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  BellRing, Mail, Send, Eye, Save, Link2, Unlink, ShieldCheck, AlertTriangle, History,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const TZ_OPTIONS = ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles"];

const stateBadge = {
  unpaid: "bg-red-50 text-red-700 dark:bg-red-500/10",
  partial: "bg-amber-50 text-amber-700 dark:bg-amber-500/10",
  paid: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10",
};

export default function RentReminders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canWrite = ["admin", "manager"].includes(user?.role);
  const [s, setS] = useState(null);
  const [rent, setRent] = useState(null);
  const [comms, setComms] = useState([]);
  const [gmail, setGmail] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/settings/rent-automation").then((r) => setS(r.data)).catch(() => {});
    api.get("/rent-status").then((r) => setRent(r.data)).catch(() => {});
    api.get("/communications").then((r) => setComms(r.data)).catch(() => {});
    api.get("/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadAll();
    const p = new URLSearchParams(window.location.search).get("gmail");
    if (p === "connected") toast.success("Gmail connected successfully");
    if (p === "error") toast.error("Gmail connection failed");
  }, [loadAll]);

  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const setTpl = (k, v) => setS((prev) => ({ ...prev, templates: { ...prev.templates, [k]: v } }));

  const save = async () => {
    try {
      const payload = {
        zelle_reading_enabled: s.zelle_reading_enabled, reminders_enabled: s.reminders_enabled,
        reminder_day: Number(s.reminder_day), timezone: s.timezone, grace_days: Number(s.grace_days),
        auto_post_threshold: Number(s.auto_post_threshold), late_fee_enabled: s.late_fee_enabled,
        late_fee_amount: Number(s.late_fee_amount || 0), late_fee_text: s.late_fee_text || "",
        excluded_tenant_ids: s.excluded_tenant_ids || [], templates: s.templates,
      };
      const { data } = await api.put("/settings/rent-automation", payload);
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleExclude = (tid) => {
    const cur = new Set(s.excluded_tenant_ids || []);
    cur.has(tid) ? cur.delete(tid) : cur.add(tid);
    set("excluded_tenant_ids", Array.from(cur));
  };

  const runReminders = async () => {
    if (!window.confirm("Send rent reminders now to all unpaid / partially-paid tenants?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/reminders/run");
      toast.success(`Reminders: ${data.sent} sent, ${data.failed} failed, ${data.skipped} skipped`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const doPreview = async (tenant_id) => {
    try {
      const { data } = await api.post("/reminders/preview", { tenant_id });
      if (!data.results.length) { toast.info("This tenant is fully paid — no reminder needed."); return; }
      setPreview(data.results[0]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const connectGmail = async () => {
    try {
      const { data } = await api.get("/oauth/gmail/login");
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const disconnectGmail = async () => {
    try { await api.post("/gmail/disconnect"); toast.success("Gmail disconnected"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const Row = ({ label, desc, children }) => (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-border last:border-0">
      <div><p className="text-sm font-medium">{label}</p>{desc && <p className="text-xs text-muted-foreground">{desc}</p>}</div>
      <div className="shrink-0">{children}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="page-rent-reminders">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Automated Rent</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><BellRing className="w-7 h-7 text-primary" /> Rent Reminders</h1>
          <p className="text-sm text-muted-foreground mt-1">Automatic reminders to tenants with unpaid or partial rent. Default send date is the 3rd of each month.</p>
        </div>
        {canWrite && (
          <button onClick={runReminders} disabled={sending} data-testid="reminders-run-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50">
            <Send className={`w-4 h-4 ${sending ? "animate-pulse" : ""}`} /> Send Reminders Now
          </button>
        )}
      </div>

      {/* Gmail connection */}
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="gmail-connection">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${gmail?.connected ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10" : "bg-muted text-muted-foreground"}`}>
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <p className="font-heading font-semibold flex items-center gap-2">Gmail Connection {gmail?.connected && <ShieldCheck className="w-4 h-4 text-emerald-500" />}</p>
              <p className="text-sm text-muted-foreground">{gmail?.connected ? `Connected as ${gmail.email}` : gmail?.configured ? "Not connected — connect to read Zelle emails & send reminders." : "Gmail credentials not configured yet (paste-email mode works without it)."}</p>
            </div>
          </div>
          {isAdmin && (gmail?.connected ? (
            <button onClick={disconnectGmail} data-testid="gmail-disconnect-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-muted"><Unlink className="w-4 h-4" /> Disconnect</button>
          ) : (
            <button onClick={connectGmail} data-testid="gmail-connect-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"><Link2 className="w-4 h-4" /> Connect Gmail</button>
          ))}
        </div>
      </section>

      {/* Automation controls */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-heading font-semibold mb-2">Automation Controls</h3>
        <Row label="Read Zelle emails automatically" desc="Poll Gmail 3× daily for payment confirmations">
          <Switch checked={s.zelle_reading_enabled} onCheckedChange={(v) => set("zelle_reading_enabled", v)} disabled={!isAdmin} data-testid="toggle-zelle-reading" />
        </Row>
        <Row label="Send rent reminders automatically" desc="Email unpaid/partial tenants on the reminder date">
          <Switch checked={s.reminders_enabled} onCheckedChange={(v) => set("reminders_enabled", v)} disabled={!isAdmin} data-testid="toggle-reminders" />
        </Row>
        <Row label="Reminder day of month" desc="Default is the 3rd">
          <input type="number" min={1} max={28} value={s.reminder_day} onChange={(e) => set("reminder_day", e.target.value)} disabled={!isAdmin} data-testid="input-reminder-day" className="w-20 rounded-lg border border-border bg-background p-2 text-sm text-right" />
        </Row>
        <Row label="Timezone">
          <select value={s.timezone} onChange={(e) => set("timezone", e.target.value)} disabled={!isAdmin} data-testid="select-timezone" className="rounded-lg border border-border bg-background p-2 text-sm">
            {TZ_OPTIONS.map((t) => <option key={t} value={t}>{t.replace("America/", "")}</option>)}
          </select>
        </Row>
        <Row label="Grace period (days after rent due)" desc="Rent is overdue this many days after the lease start day">
          <input type="number" min={0} max={31} value={s.grace_days} onChange={(e) => set("grace_days", e.target.value)} disabled={!isAdmin} data-testid="input-grace-days" className="w-20 rounded-lg border border-border bg-background p-2 text-sm text-right" />
        </Row>
        <Row label="Auto-post confidence threshold (%)" desc="Zelle matches at or above this are posted automatically">
          <input type="number" min={50} max={100} value={s.auto_post_threshold} onChange={(e) => set("auto_post_threshold", e.target.value)} disabled={!isAdmin} data-testid="input-threshold" className="w-20 rounded-lg border border-border bg-background p-2 text-sm text-right" />
        </Row>
        <Row label="Include late-fee info in reminders">
          <Switch checked={s.late_fee_enabled} onCheckedChange={(v) => set("late_fee_enabled", v)} disabled={!isAdmin} data-testid="toggle-late-fee" />
        </Row>
        {s.late_fee_enabled && (
          <div className="pt-3">
            <textarea value={s.late_fee_text} onChange={(e) => set("late_fee_text", e.target.value)} disabled={!isAdmin} rows={2} data-testid="input-late-fee-text" placeholder="e.g. A late fee of $75 applies after the 5th of the month." className="w-full rounded-xl border border-border bg-background p-3 text-sm" />
          </div>
        )}
        {isAdmin && <div className="flex justify-end pt-4"><button onClick={save} data-testid="save-settings-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"><Save className="w-4 h-4" /> Save Settings</button></div>}
      </section>

      {/* Email templates */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-heading font-semibold mb-1">Reminder Email Templates</h3>
        <p className="text-xs text-muted-foreground mb-4">Use placeholders: {"{tenant_name}"}, {"{month}"}, {"{balance_due}"}.</p>
        <div className="grid md:grid-cols-2 gap-5">
          {[["Unpaid Rent", "full_subject", "full_body"], ["Partial Balance", "partial_subject", "partial_body"]].map(([title, sk, bk]) => (
            <div key={sk} className="space-y-2">
              <p className="text-sm font-semibold">{title}</p>
              <input value={s.templates[sk]} onChange={(e) => setTpl(sk, e.target.value)} disabled={!isAdmin} data-testid={`tpl-${sk}`} className="w-full rounded-lg border border-border bg-background p-2 text-sm" />
              <textarea value={s.templates[bk]} onChange={(e) => setTpl(bk, e.target.value)} disabled={!isAdmin} rows={7} data-testid={`tpl-${bk}`} className="w-full rounded-lg border border-border bg-background p-2 text-sm font-mono" />
            </div>
          ))}
        </div>
        {isAdmin && <div className="flex justify-end pt-4"><button onClick={save} data-testid="save-templates-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"><Save className="w-4 h-4" /> Save Templates</button></div>}
      </section>

      {/* Rent status this month */}
      <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="rent-status-table">
        <div className="p-5 pb-3"><h3 className="font-heading font-semibold">Rent Status — {rent?.month}</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Tenant</th>
                <th className="text-left font-semibold px-4 py-3">Property / Unit</th>
                <th className="text-right font-semibold px-4 py-3">Rent</th>
                <th className="text-right font-semibold px-4 py-3">Paid</th>
                <th className="text-right font-semibold px-4 py-3">Balance</th>
                <th className="text-left font-semibold px-4 py-3">Status</th>
                <th className="text-center font-semibold px-4 py-3">Exclude</th>
                <th className="text-right font-semibold px-4 py-3">Preview</th>
              </tr>
            </thead>
            <tbody>
              {(rent?.tenants || []).map((t) => (
                <tr key={t.tenant_id} className="border-t border-border hover:bg-muted/40" data-testid={`rent-row-${t.tenant_id}`}>
                  <td className="px-4 py-3 font-medium">{t.tenant_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.property_name} {t.unit}</td>
                  <td className="px-4 py-3 text-right">{money(t.rent)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{money(t.paid)}</td>
                  <td className="px-4 py-3 text-right font-semibold">{money(t.balance)}</td>
                  <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium capitalize ${stateBadge[t.state]}`}>{t.state}</span></td>
                  <td className="px-4 py-3 text-center"><input type="checkbox" checked={(s.excluded_tenant_ids || []).includes(t.tenant_id)} onChange={() => toggleExclude(t.tenant_id)} disabled={!isAdmin} data-testid={`exclude-${t.tenant_id}`} className="accent-primary w-4 h-4" /></td>
                  <td className="px-4 py-3 text-right">
                    {t.state !== "paid" && <button onClick={() => doPreview(t.tenant_id)} data-testid={`preview-${t.tenant_id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Eye className="w-3.5 h-3.5" /> Preview</button>}
                  </td>
                </tr>
              ))}
              {rent && rent.tenants.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No active tenants with rent due.</td></tr>}
            </tbody>
          </table>
        </div>
        {isAdmin && <div className="p-4 flex justify-end border-t border-border"><button onClick={save} className="text-xs text-primary hover:underline" data-testid="save-exclusions-btn">Save exclusions</button></div>}
      </section>

      {/* Communication history */}
      <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="communications-table">
        <div className="p-5 pb-3 flex items-center gap-2"><History className="w-5 h-5 text-primary" /><h3 className="font-heading font-semibold">Communication History</h3></div>
        <div className="overflow-x-auto max-h-[400px]">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Date</th>
                <th className="text-left font-semibold px-4 py-3">Tenant</th>
                <th className="text-left font-semibold px-4 py-3">Email</th>
                <th className="text-left font-semibold px-4 py-3">Type</th>
                <th className="text-right font-semibold px-4 py-3">Amount Due</th>
                <th className="text-left font-semibold px-4 py-3">Delivery</th>
              </tr>
            </thead>
            <tbody>
              {comms.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No reminders sent yet.</td></tr>}
              {comms.map((c) => (
                <tr key={c.id} className="border-t border-border" data-testid={`comm-row-${c.id}`}>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{String(c.date_sent).slice(0, 10)}</td>
                  <td className="px-4 py-3">{c.tenant_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.email}</td>
                  <td className="px-4 py-3">{c.reminder_type}</td>
                  <td className="px-4 py-3 text-right">{money(c.amount_due)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${c.delivery_status === "Sent" ? "text-emerald-600" : c.delivery_status?.startsWith("Failed") ? "text-red-600" : "text-amber-600"}`}>
                      {c.delivery_status?.startsWith("Failed") && <AlertTriangle className="w-3 h-3" />}{c.delivery_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="preview-dialog">
          <DialogHeader><DialogTitle>Reminder Preview — {preview?.tenant}</DialogTitle><DialogDescription>This is exactly what the tenant will receive. It has not been sent.</DialogDescription></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <p><span className="text-muted-foreground">To:</span> {preview.email || "(no email on file)"}</p>
              <p><span className="text-muted-foreground">Subject:</span> <span className="font-medium">{preview.subject}</span></p>
              <div className="rounded-xl bg-muted/50 p-4 whitespace-pre-wrap text-sm">{preview.body}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
